import range from "lodash/range";
import {
  AuthCategory,
  MissingInformationOutreachConfigurationResponse,
  ProcedureCode,
} from "@coherehealth/core-platform-api";
import {
  AuditOutcome,
  CaseType,
  DisplayConfig,
  QueueViewState,
  ReviewOutcome,
  ServiceCase,
  TablePageViewState,
  ColumnConfig,
  User,
  UserSkill,
  ServiceCaseUserHold,
  PatientName,
} from "@coherehealth/qm-api";

import {
  formatDateStrWithCustomFormat,
  formatDateWithCustomFormat,
  colorsLight,
  Pill,
  Caption,
  ClipboardIcon,
  MedicalServicesIcon,
  LetterWritingIcon,
  diffDatefromNow,
  caseTypeLabel,
  commaSeparatedStrings,
  Body1,
  Column,
  SelectOptionsHook,
  DropdownOption,
  isTimestamp,
  FilterConfig,
  Chip,
  FilterValues,
  POLICY_UNITED_STATES_OPTIONS,
} from "@coherehealth/common";
import { Print, Phone } from "@material-ui/icons";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { FunctionComponent, ReactNode } from "react";
import { utcToZonedTime } from "date-fns-tz";

export const tableColumnsConfig = (
  queueView: QueueViewState,
  pageView: TablePageViewState,
  isCohereUser: boolean = false,
  displayConfig?: DisplayConfig,
  missingInfoConfigs?: Record<string, MissingInformationOutreachConfigurationResponse>
): Column<ServiceCase>[] => {
  const columnConfig = columnNameLabelConfig(queueView, pageView, displayConfig);

  return columnConfig.map((column) => {
    return {
      label: column.label,
      name: column.name,
      width: column.width || columnWidthConfig(column.name, pageView),
      value: columnValueConfig(column.name, isCohereUser, pageView, displayConfig, missingInfoConfigs),
      themedPaddingRight: 1,
    };
  });
};

export const userHoldsTableColumnsConfig = (): Column<ServiceCaseUserHold>[] => {
  const columnConfig = userHoldColumnNameLabelConfig();

  return columnConfig.map((column) => {
    return {
      label: column.label,
      name: column.name,
      width: column.width || "20%",
      value: userHoldColumnValueConfig(column.name),
      themedPaddingRight: 0,
    };
  });
};

const outreachAttemptOptions: DropdownOption[] = range(0, 4)
  .map((i: number) => {
    return {
      id: `${i}`,
      label: outreachLabel(i),
    };
  })
  .concat({
    id: "4",
    label: "MI 4+ attempt",
  });

const requestTypeOptions: DropdownOption[] = [
  { id: "INITIAL", label: "Initial admission" },
  { id: "CONTINUATION", label: "Continued Stay" },
];

const hasNewAttachmentsOptions: DropdownOption[] = [
  { id: "true", label: "Yes" },
  { id: "false", label: "No" },
];

export const getCaseTypeOptions = (facilityBasedConfig?: any): DropdownOption[] => {
  const allOptions = [
    { id: "FAX_INTAKE", label: caseTypeLabel("FAX_INTAKE") },
    { id: "RN_REVIEW", label: caseTypeLabel("RN_REVIEW") },
    { id: "MD_REVIEW", label: caseTypeLabel("MD_REVIEW") },
    { id: "ADMIN_REVIEW", label: caseTypeLabel("ADMIN_REVIEW") },
    { id: "LETTER_WRITING", label: caseTypeLabel("LETTER_WRITING") },
    { id: "MISSING_INFORMATION", label: caseTypeLabel("MISSING_INFORMATION") },
    { id: "FOLLOW_UP", label: caseTypeLabel("FOLLOW_UP") },
  ];

  return !!facilityBasedConfig?.enabled ? allOptions : allOptions.filter((option) => option.id !== "FOLLOW_UP");
};

// Not yet supported by server
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const timeSinceLastOutreachOptions: DropdownOption[] = [
  {
    id: "15m",
    label: "15 minutes",
  },
  {
    id: "30m",
    label: "40 minutes",
  },
  {
    id: "1h",
    label: "1 hour",
  },
  {
    id: "6h",
    label: "6 hours",
  },
  {
    id: "12h",
    label: "12 hours",
  },
  {
    id: "1d",
    label: "1 day",
  },
  {
    id: "3+d",
    label: "3+ days",
  },
];

export interface FilterConfigInjectedDeps {
  useUsers: SelectOptionsHook<User>;
  usePatients: SelectOptionsHook<PatientName>;
  useProcedures: SelectOptionsHook<ProcedureCode | null>;
  useSkillSearch: SelectOptionsHook<UserSkill>;
  authCategoriesOptions: { id: AuthCategory; label: string | undefined }[];
  allowFilteringOnNewServiceCaseFields?: boolean;
  caseHoldReasonOptions?: DropdownOption[];
  facilitybasedconfig?: any;
}

export const NO_REQUIRED_SKILLS_VALUE = "NO_REQUIRED_SKILLS";
export const NO_REQUIRED_SKILLS_LABEL = "No required skills";

export function tableColumnToFilterConfig(
  deps: FilterConfigInjectedDeps,
  column: Column<ServiceCase>,
  currentFilterValues?: FilterValues
): FilterConfig | null {
  const useUsers = deps.useUsers as SelectOptionsHook<unknown>;
  const usePatientNames = deps.usePatients as SelectOptionsHook<unknown>;
  const useSkillSearch = deps.useSkillSearch as SelectOptionsHook<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const useProcedures = deps.useProcedures as SelectOptionsHook<unknown>;
  const allowNewFilters = deps.allowFilteringOnNewServiceCaseFields;
  const baseConfig = {
    key: column.name,
    label: column.label,
  };
  switch (column.name) {
    case "dueDate": {
      return {
        ...baseConfig,
        filterType: "DatePicker",
        label: `${column.label} by`,
        initialFilterValue: currentFilterValues?.["dueDate"],
      } as FilterConfig;
    }
    case "isFlaggedAsUrgent": {
      return {
        ...baseConfig,
        filterType: "Checkbox",
        label: "Expedited",
        apiParamName: "isFlaggedAsUrgent",
        initialFilterValue: currentFilterValues?.["isFlaggedAsUrgent"],
      } as FilterConfig;
    }
    case "dateCreated": {
      return {
        ...baseConfig,
        filterType: "DatePicker",
        label: `${column.label} after`,
        apiParamName: "dateCreatedAfter",
        initialFilterValue: currentFilterValues?.["dateCreated"],
      } as FilterConfig;
    }
    case "serviceRequest.tatDueTimestamp": {
      return {
        ...baseConfig,
        filterType: "DatePicker",
        label: `${column.label} by`,
        apiParamName: "tatDueBefore",
        initialFilterValue: currentFilterValues?.["serviceRequest.tatDueTimestamp"],
      } as FilterConfig;
    }
    case "assignee.name": {
      return {
        ...baseConfig,
        filterType: "LookupSearch",
        apiParamName: "assigneeNameList",
        useOptions: useUsers,
        optionToLabel: ({ name }: User) => name || "",
        optionToParamValue: ({ name }: User) => name,
        initialFilterValues: currentFilterValues?.["assignee.name"],
        isLazyLoaded: false,
      } as FilterConfig;
    }
    case "lastReviewedBy": {
      return {
        ...baseConfig,
        filterType: "LookupSearch",
        useOptions: useUsers,
        apiParamName: "lastReviewedByList",
        optionToLabel: ({ name }: User) => name || "",
        optionToParamValue: ({ name }: User) => name,
        initialFilterValues: currentFilterValues?.["lastReviewedBy"],
        isLazyLoaded: false,
      } as FilterConfig;
    }
    case "serviceRequest.lastSuccessfulMICOutreachAttempt": {
      return {
        ...baseConfig,
        filterType: "MultiSelect",
        apiParamName: "outreachAttemptList",
        options: outreachAttemptOptions,
        initialFilterValues: currentFilterValues?.["serviceRequest.lastSuccessfulMICOutreachAttempt"],
      } as FilterConfig;
    }
    case "requiredSkills": {
      return {
        ...baseConfig,
        filterType: "LookupSearch",
        useOptions: useSkillSearch,
        apiParamName: "requiredSkillsList",
        optionToLabel: ({ name }: UserSkill) =>
          name === NO_REQUIRED_SKILLS_VALUE ? NO_REQUIRED_SKILLS_LABEL : name || "",
        optionToParamValue: ({ name }: UserSkill) => name,
        initialFilterValues: currentFilterValues?.["requiredSkills"],
        isLazyLoaded: false,
      } as FilterConfig;
    }
    case "caseNumber": {
      return {
        ...baseConfig,
        filterType: "TextField",
        apiParamName: "caseNumberList",
        postProcessValue: (value: string[]) => value.map((s) => s.toUpperCase().trim()),
        initialFilterValue: currentFilterValues?.["caseNumber"],
      } as FilterConfig;
    }
    case "serviceRequest.cohereId": {
      return {
        ...baseConfig,
        filterType: "TextField",
        apiParamName: "trackingNumberList",
        postProcessValue: (value: string[]) => value.map((s) => s.toUpperCase().trim()),
        initialFilterValue: currentFilterValues?.["serviceRequest.cohereId"],
      } as FilterConfig;
    }
    case "serviceRequest.authNumber": {
      return {
        ...baseConfig,
        filterType: "TextField",
        apiParamName: "authNumberList",
        postProcessValue: (value: string[]) => value.map((s) => s.toUpperCase().trim()),
        initialFilterValue: currentFilterValues?.["serviceRequest.authNumber"],
      } as FilterConfig;
    }
    case "serviceRequest.nextReviewDateTimestamp": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "DatePicker",
          label: `${column.label} after`,
          apiParamName: "nextReviewDateAfter",
          initialFilterValue: currentFilterValues?.["serviceRequest.nextReviewDateTimestamp"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    // to be fully implemented in COD-4634
    case "serviceRequest.facilityName": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "TextField",
          apiParamName: "facilityNameList",
          postProcessValue: (value: string[]) => value.map((s) => s.trim()),
          initialFilterValue: currentFilterValues?.["serviceRequest.facilityName"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    // to be fully implemented in COD-4634
    case "serviceRequest.facilityNpi": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "TextField",
          apiParamName: "facilityNpiList",
          postProcessValue: (value: string[]) => value.map((s) => s.toUpperCase().trim()),
          initialFilterValue: currentFilterValues?.["serviceRequest.facilityNpi"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "serviceRequest.requestType": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "MultiSelect",
          apiParamName: "requestTypeList",
          options: requestTypeOptions,
          initialFilterValues: currentFilterValues?.["serviceRequest.requestType"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "serviceRequest.authCategory": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "LookupSearch",
          apiParamName: "authCategoryList",
          useOptions: () => ({
            options: deps.authCategoriesOptions,
            optionsLoading: false,
            filterOptions: (options: { id: AuthCategory; label: string | undefined }[], state: { inputValue: any }) =>
              options.filter((option) => option?.label?.toLowerCase().includes(state?.inputValue?.toLowerCase())),
          }),
          optionToLabel: (option: { id: AuthCategory; label: string | undefined }) => option?.label,
          optionToParamValue: ({ id }: { id: AuthCategory; label: string | undefined }) => id,
          isLazyLoaded: false,
          initialFilterValues: currentFilterValues?.["serviceRequest.authCategory"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "caseType": {
      if (allowNewFilters) {
        const caseTypeOptions = getCaseTypeOptions(deps.facilitybasedconfig);
        return {
          ...baseConfig,
          filterType: "MultiSelect",
          apiParamName: "caseTypeList",
          options: caseTypeOptions,
          initialFilterValues: currentFilterValues?.["caseType"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "hasNewAttachments": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "MultiSelect",
          apiParamName: "hasNewAttachments",
          options: hasNewAttachmentsOptions,
          initialFilterValues: currentFilterValues?.["hasNewAttachments"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "serviceRequest.tatStartTimestamp": {
      if (allowNewFilters) {
        return {
          ...baseConfig,
          filterType: "DatePicker",
          label: `${column.label} after`,
          apiParamName: "tatStartTimeAfter",
          initialFilterValue: currentFilterValues?.["serviceRequest.tatStartTimestamp"],
        } as FilterConfig;
      } else {
        return null;
      }
    }
    case "serviceRequest.stateOfIssue": {
      return {
        ...baseConfig,
        filterType: "MultiSelect",
        apiParamName: "stateOfIssueList",
        options: POLICY_UNITED_STATES_OPTIONS,
        initialFilterValues: currentFilterValues?.["serviceRequest.stateOfIssue"],
      } as FilterConfig;
    }
    case "activeCaseHold.reason": {
      return {
        ...baseConfig,
        filterType: "MultiSelect",
        apiParamName: "activeCaseHoldReasonList",
        options: deps.caseHoldReasonOptions,
        initialFilterValues: currentFilterValues?.["activeCaseHold.reason"],
      } as FilterConfig;
    }
    case "serviceRequest.patientName": {
      return {
        ...baseConfig,
        filterType: "LookupSearch",
        apiParamName: "patientName",
        useOptions: usePatientNames,
        optionToLabel: ({ name }: User) => name || "",
        optionToParamValue: ({ name }: User) => name,
        initialFilterValues: currentFilterValues?.["serviceRequest.patientName"],
        isLazyLoaded: false,
        maxSelections: 1,
      } as FilterConfig;
    }
    // These are not yet supported by qm-service so do not show in UI.
    //
    // case "lastSuccessfulMICOutreachAttempt.completedDate": {
    //   return {
    //     ...baseConfig,
    //     filterType: "SingleSelect",
    //     apiParamName: "timeSinceLastOutreach",
    //     options: timeSinceLastOutreachOptions,
    //   } as FilterConfig;
    // }
    // case "serviceRequest.semanticProcedureCodes.0.code": {
    //   return {
    //     ...baseConfig,
    //     filterType: "LookupSearch",
    //     apiParamName: "procedureCodeList",
    //     useOptions: useProcedures,
    //     optionToLabel: ({ code }: ProcedureCode) => code,
    //     optionToParamValue: ({ code }: ProcedureCode) => code,
    //   } as FilterConfig;
    // }
    default: {
      return null;
    }
  }
}

const columnNameLabelConfig = (
  queueView: QueueViewState,
  pageView: TablePageViewState,
  displayConfig?: DisplayConfig
): ColumnConfig[] => {
  if (
    (pageView === "SUPERVISOR_DASHBOARD" && displayConfig?.supervisorTableColumnConfigs) ||
    (pageView === "AGENT_DASHBOARD" && displayConfig?.agentHistoryTableColumnConfigs)
  ) {
    return columnConfigFromDisplayConfig(pageView, displayConfig);
  }
  switch (pageView) {
    case "SUPERVISOR_DASHBOARD":
      switch (queueView) {
        case "OUTREACH":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Work due", name: "dueDate" },
            { label: "Outreach status", name: "serviceRequest.lastSuccessfulMICOutreachAttempt" },
            { label: "Last worked by", name: "lastReviewedBy" },
            { label: "Assignee", name: "assignee.name" },
            { label: "Time since last outreach", name: "lastSuccessfulMICOutreachAttempt.completedDate" },
          ];
        case "LETTER_WRITING":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Skill category", name: "requiredSkills" },
            { label: "Last clinical reviewer", name: "lastReviewedBy" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "P2P_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Skill category", name: "requiredSkills" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "NEW_HIRE_AUDIT_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Last worked by", name: "lastReviewedBy" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "MD_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Skill category", name: "requiredSkills" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "RN_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "TAT due", name: "serviceRequest.tatDueTimestamp" },
            { label: "Work due", name: "dueDate" },
            { label: "Skill category", name: "requiredSkills" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "CPP_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "TAT due", name: "serviceRequest.tatDueTimestamp" },
            { label: "Work due", name: "dueDate" },
            { label: "Skill category", name: "requiredSkills" },
            { label: "Assignee", name: "assignee.name" },
          ];
        case "FAX_INTAKE":
          return [
            { label: "Case ID", name: "caseNumber" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Assignee", name: "assignee.name" },
            { label: "Urgency", name: "isFlaggedAsUrgent" },
          ];
        case "CONCURRENT_LETTER_WRITING":
        case "CONCURRENT_MD_REVIEW":
        case "CONCURRENT_OUTREACH":
        case "CONCURRENT_P2P_REVIEW":
        case "CONCURRENT_RN_REVIEW":
        case "REQUIRES_VERBAL_NOTIFICATIONS":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Next review date", name: "serviceRequest.nextReviewDateTimestamp" },
            { label: "Facility name", name: "serviceRequest.facilityName" },
            { label: "NPI", name: "serviceRequest.facilityNpi" },
            { label: "Request type", name: "serviceRequest.requestType" },
            { label: "Assignee", name: "assignee.name" },
          ];
        default:
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work due", name: "dueDate" },
            { label: "Assignee", name: "assignee.name" },
          ];
      }
    case "AGENT_DASHBOARD":
      switch (queueView) {
        case "LETTER_WRITING":
        case "CONCURRENT_LETTER_WRITING":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        case "P2P_REVIEW":
        case "CONCURRENT_P2P_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        case "MD_REVIEW":
        case "CONCURRENT_MD_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        case "RN_REVIEW":
        case "CONCURRENT_RN_REVIEW":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        case "FAX_INTAKE":
          return [
            { label: "Case ID", name: "caseNumber" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        case "CONCURRENT_OUTREACH":
        case "REQUIRES_VERBAL_NOTIFICATIONS":
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
        default:
          return [
            { label: "Tracking #", name: "serviceRequest.cohereId" },
            { label: "Type", name: "caseType" },
            { label: "Request date", name: "dateCreated" },
            { label: "Work complete", name: "dateCompleted" },
          ];
      }
  }
};

const userHoldColumnNameLabelConfig = (): ColumnConfig[] => {
  return [
    { label: "Tracking #", name: "serviceCase.serviceRequest.cohereId", width: "15%" },
    { label: "Hold reason", name: "otherReason", width: "350px" },
    { label: "On hold since", name: "heldAt", width: "20%" },
    { label: "Hold status", name: "status", width: "40%" },
  ];
};

const columnConfigFromDisplayConfig = (pageView: TablePageViewState, displayConfig: DisplayConfig) => {
  switch (pageView) {
    case "SUPERVISOR_DASHBOARD":
      let supervisorColumns: ColumnConfig[] = [];
      displayConfig.supervisorTableColumnConfigs?.forEach((columnConfig) => {
        const formattedColumnConfig = formatColumnConfig(columnConfig);
        if (formattedColumnConfig) {
          supervisorColumns.push(formattedColumnConfig);
        }
      });
      return supervisorColumns;
    case "AGENT_DASHBOARD":
      let agentColumns: ColumnConfig[] = [];
      displayConfig.agentHistoryTableColumnConfigs?.forEach((columnConfig) => {
        const formattedColumnConfig = formatColumnConfig(columnConfig);
        if (formattedColumnConfig) {
          agentColumns.push(columnConfig);
        }
      });
      return agentColumns;
  }
};

const formatColumnConfig = (columnConfig: ColumnConfig): ColumnConfig | undefined => {
  const { name, label } = columnConfig;

  if (name && label) {
    return columnConfig;
  }
  if (name) {
    switch (name) {
      case "caseNumber":
        return { ...columnConfig, label: "Case ID" };
      case "serviceRequest.cohereId":
        return { ...columnConfig, label: "Tracking #" };
      case "serviceRequest.patientName":
        return { ...columnConfig, label: "Patient Name" };
      case "dueDate":
        return { ...columnConfig, label: "Work due" };
      case "assignee.name":
        return { ...columnConfig, label: "Assignee" };
      case "lastReviewedBy":
        return { ...columnConfig, label: "Last worked by" };
      case "lastSuccessfulMICOutreachAttempt.completedDate":
        return { ...columnConfig, label: "Time since last outreach" };
      case "dateCreated":
        return { ...columnConfig, label: "Request date" };
      case "requiredSkills":
        return { ...columnConfig, label: "Skill category" };
      case "serviceRequest.tatDueTimestamp":
        return { ...columnConfig, label: "TAT due" };
      case "serviceRequest.nextReviewDateTimestamp":
        return { ...columnConfig, label: "Next review date" };
      case "serviceRequest.facilityName":
        return { ...columnConfig, label: "Facility name" };
      case "serviceRequest.facilityNpi":
        return { ...columnConfig, label: "NPI" };
      case "serviceRequest.requestType":
        return { ...columnConfig, label: "Request type" };
      case "dateCompleted":
        return { ...columnConfig, label: "Work complete" };
      case "caseType":
        return { ...columnConfig, label: "Type" };
      case "serviceRequest.lastSuccessfulMICOutreachAttempt":
        return { ...columnConfig, label: "Outreach status" };
      case "activeCaseHold.reason":
        return { ...columnConfig, label: "Hold Reason" };
      case "activeCaseHold.expiry":
        return { ...columnConfig, label: "Time Left on Hold" };
      default:
        return { ...columnConfig, label: formatColumnName(name) };
    }
  }
};

const formatColumnName = (columnName: string) => {
  const splitString = columnName.split(".");
  const [firstName, ...rest] = splitString;
  const formattedString = `${capitalizeString(firstName)} ${rest.join(" ")}`;
  return formattedString;
};

const capitalizeString = (string: string, downcaseRemainder: boolean = false): string => {
  return string.charAt(0).toUpperCase() + (downcaseRemainder ? string.slice(1).toLowerCase() : string.slice(1));
};
const capitalizeAndRemoveUnderscore = (str: string) =>
  str
    .split("_")
    .map((substr) => capitalizeString(substr, true))
    .join(" ");

const columnWidthConfig = (columnName: string, pageView: TablePageViewState) => {
  switch (pageView) {
    case "SUPERVISOR_DASHBOARD":
      switch (columnName) {
        case "serviceRequest.cohereId":
        case "caseNumber":
        case "serviceRequest.facilityNpi":
          return "15%";
        case "dateCreated":
          return "20%";
        case "requiredSkills":
        case "lastReviewedBy":
        case "dueDate":
          return "20%";
        case "serviceRequest.tatDueTimestamp":
          return "20%";
        case "Assignee":
        case "serviceRequest.patientName":
          return "25%";
        case "serviceRequest.nextReviewDateTimestamp":
          return "20%";
        case "serviceRequest.facilityName":
          return "35%";
        default:
          return "20%";
      }
    case "AGENT_DASHBOARD":
      switch (columnName) {
        case "serviceRequest.cohereId":
        case "caseNumber":
        case "caseType":
        case "serviceRequest.facilityNpi":
          return "15%";
        case "serviceRequest.nextReviewDateTimestamp":
          return "20%";
        case "dateCreated":
        case "dateCompleted":
        case "serviceRequest.facilityName":
          return "35%";
        default:
          return "20%";
      }
    default:
      return "20%";
  }
};

const columnValueConfig = (
  columnName: string,
  isCohereUser: boolean,
  pageView: TablePageViewState,
  displayConfig?: DisplayConfig,
  missingInfoConfigs?: Record<string, MissingInformationOutreachConfigurationResponse>
): ((serviceCase: ServiceCase) => ReactNode) => {
  switch (columnName) {
    case "serviceRequest.cohereId":
      return (serviceCase: ServiceCase) => serviceCase.serviceRequest?.cohereId;
    case "serviceRequest.authNumber":
      return (serviceCase: ServiceCase) => serviceCase.serviceRequest?.authNumber?.toUpperCase();
    case "serviceRequest.patientName":
      return (serviceCase: ServiceCase) => toTitleCase(serviceCase.serviceRequest?.patientName);
    case "caseNumber":
      return (serviceCase: ServiceCase) => serviceCase.caseNumber;
    case "dateCreated":
      return (serviceCase: ServiceCase) =>
        formatDateStrWithCustomFormat(serviceCase.dateCreated, "MM/dd/yyyy - hh:mma");
    case "dueDate":
      return (serviceCase: ServiceCase) =>
        dueDateValueWithOptionalEscalatedChip(serviceCase, isCohereUser, displayConfig);
    case "serviceRequest.tatDueTimestamp":
      return (serviceCase: ServiceCase) =>
        formatDateStrWithCustomFormat(serviceCase.serviceRequest?.tatDueTimestamp, "MM/dd/yyyy - hh:mma");
    case "requiredSkills":
      return (serviceCase: ServiceCase) => {
        return serviceCase.requiredSkills ? commaSeparatedStrings(serviceCase.requiredSkills) : "--";
      };
    case "lastReviewedBy":
      return (serviceCase: ServiceCase) => serviceCase.lastReviewedBy;
    case "lastSuccessfulMICOutreachAttempt.completedDate":
      return (serviceCase: ServiceCase) =>
        serviceCase.serviceRequest?.lastSuccessfulMICOutreachAttempt?.completedDate
          ? timeSinceOutreach(serviceCase.serviceRequest.lastSuccessfulMICOutreachAttempt.completedDate)
          : "--";
    case "assignee.name":
      return () => <></>;
    case "serviceRequest.nextReviewDateTimestamp":
      return (serviceCase: ServiceCase) => {
        const dateString = serviceCase.serviceRequest?.nextReviewDateTimestamp;
        const date = dateString ? utcToZonedTime(new Date(dateString), "UTC") : undefined;
        return formatDateWithCustomFormat(date, "MM/dd/yyyy");
      };
    case "serviceRequest.requestType":
      return (serviceCase: ServiceCase) => {
        const isContinuation = serviceCase?.serviceRequest?.requestType === "CONTINUATION";
        const isFollowUp = serviceCase?.caseType === "FOLLOW_UP";
        return isContinuation || isFollowUp ? "Continued stay" : "Initial admission";
      };
    case "serviceRequest.requestClassification":
      return (serviceCase: ServiceCase) =>
        capitalizeAndRemoveUnderscore(serviceCase?.serviceRequest?.requestClassification || "--");
    case "serviceRequest.facilityName":
      return (serviceCase: ServiceCase) => serviceCase.serviceRequest?.facilityName;
    case "serviceRequest.facilityNpi":
      return (serviceCase: ServiceCase) => serviceCase.serviceRequest?.facilityNpi;
    case "caseType":
      return (serviceCase: ServiceCase) => caseTypeValue(serviceCase.caseType);
    case "dateCompleted":
      return (serviceCase: ServiceCase) => workCompleteValue(serviceCase, displayConfig);
    case "serviceRequest.lastSuccessfulMICOutreachAttempt":
      return (serviceCase: ServiceCase) => outreachStatusValue(serviceCase, missingInfoConfigs || {});
    case "hasNewAttachments":
      return (serviceCase: ServiceCase) => hasNewAttachmentsValue(serviceCase);
    case "activeCaseHold.reason":
      return (serviceCase: ServiceCase) =>
        serviceCase.activeCaseHold?.otherReason
          ? `${serviceCase.activeCaseHold?.reason} - ${serviceCase.activeCaseHold?.otherReason}`
          : serviceCase.activeCaseHold?.reason;
    case "activeCaseHold.expiry":
      return (serviceCase: ServiceCase) => getHoldExpiryData(serviceCase.activeCaseHold?.expiry)?.timeRemaining;
    case "isFlaggedAsUrgent":
      return (serviceCase: ServiceCase) => isFlaggedAsUrgentChip(serviceCase, isCohereUser, displayConfig);
    case "fileReceived":
      return (serviceCase: ServiceCase) =>
        serviceCase?.fileDetail?.fileReceivedTime
          ? new Date(serviceCase.fileDetail.fileReceivedTime).toLocaleString()
          : "N/A";
    default:
      let useSentenceCase = true;

      if (
        (pageView === "SUPERVISOR_DASHBOARD" && displayConfig?.supervisorTableColumnConfigs) ||
        (pageView === "AGENT_DASHBOARD" && displayConfig?.agentHistoryTableColumnConfigs)
      ) {
        const columnConfigs = columnConfigFromDisplayConfig(pageView, displayConfig);

        columnConfigs.forEach((columnConfig) => {
          if (columnConfig.name === columnName && columnConfig.useSentenceCase !== undefined) {
            useSentenceCase = columnConfig.useSentenceCase;
          }
        });
      }

      return (serviceCase: ServiceCase) => generateDefaultValue(serviceCase, columnName, useSentenceCase);
  }
};

const userHoldColumnValueConfig = (columnName: string): ((hold: ServiceCaseUserHold) => ReactNode) => {
  switch (columnName) {
    case "serviceCase.serviceRequest.cohereId":
      return (hold: ServiceCaseUserHold) => hold.serviceCase?.serviceRequest?.cohereId;
    case "otherReason":
      return (hold: ServiceCaseUserHold) => hold.otherReason ?? hold.reason;
    case "heldAt":
      return (hold: ServiceCaseUserHold) => formatDateStrWithCustomFormat(hold.heldAt, "MM/dd/yyyy - hh:mma");
    case "status":
      return (hold: ServiceCaseUserHold) => formatHoldStatus(hold.expiry);
    default:
      return (hold: ServiceCaseUserHold) => null;
  }
};

export const formatHoldStatus = (expiry: string | undefined): ReactNode => {
  const expiryData = getHoldExpiryData(expiry);
  if (!expiryData) {
    return "--";
  }
  const { days, timeRemaining } = expiryData;
  const variant = days < 2 ? "error" : "warning";
  return <Pill label={`${timeRemaining} remaining on hold`} variant={variant} />;
};

const getHoldExpiryData = (expiry: string | undefined): { days: number; timeRemaining: string } | undefined => {
  if (!expiry) {
    return undefined;
  }
  const millisUntilHoldEnd = new Date(expiry).getTime() - Date.now();
  const hoursUntilHoldEnd = Math.floor(millisUntilHoldEnd / (60 * 60 * 1000));
  const days = Math.floor(hoursUntilHoldEnd / 24);
  const hours = hoursUntilHoldEnd % 24;
  const timeRemaining = getTimeRemainingLabel(days, hours);
  return { days, timeRemaining };
};

function getTimeRemainingLabel(days: number, hours: number): string {
  if (days >= 1) {
    const dayLabel = days > 1 ? "days" : "day";
    return `${days} ${dayLabel}`;
  } else if (hours > 0) {
    const hourLabel = hours > 1 ? "hours" : "hour";
    return `${hours} ${hourLabel}`;
  } else {
    return "< 1 hour";
  }
}

const generateDefaultValue = (serviceCase: ServiceCase, columnName: string, useSentenceCase?: boolean) => {
  const fieldNames = columnName.split(".");
  const [firstField, ...rest] = fieldNames;
  return recursiveIndex(firstField, rest, serviceCase, useSentenceCase);
};

const recursiveIndex = <T,>(
  fieldName: string,
  others: string[],
  object?: GenericObject<T>,
  useSentenceCase?: boolean
): ReactNode => {
  if (object) {
    if (isFieldNameValid(fieldName, object)) {
      const nestedObject = object[fieldName];
      if (nestedObject) {
        if (others.length > 0) {
          const [nestedField, ...rest] = others;
          return recursiveIndex(nestedField, rest, nestedObject, useSentenceCase);
        } else {
          if (Array.isArray(nestedObject)) {
            return commaSeparatedStrings(nestedObject);
          } else if (
            typeof nestedObject === "string" &&
            formatDateStrWithCustomFormat(nestedObject, "MM/dd/yyyy - hh:mma")
          ) {
            return formatDateStrWithCustomFormat(nestedObject, "MM/dd/yyyy - hh:mma");
          } else if (typeof nestedObject === "number" && isTimestamp(nestedObject)) {
            return formatDateWithCustomFormat(new Date(nestedObject), "MM/dd/yyyy - hh:mma");
          } else {
            if (useSentenceCase) {
              return toSentenceCase(`${nestedObject}`);
            } else {
              return `${nestedObject}`;
            }
          }
        }
      }
    }
  }
};

const toTitleCase = (value?: string) => {
  if (!value) {
    return "";
  }

  return value
    .split(/\s+/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
};

type GenericObject<T> = Partial<Record<keyof T, T[keyof T]>>;

const isFieldNameValid = <T,>(
  fieldName: string | number | symbol,
  object: GenericObject<T>
): fieldName is keyof typeof object => {
  if (typeof object === "string") {
    return false;
  }
  return fieldName in object;
};

const caseTypeValue = (caseType?: CaseType) => {
  if (!caseType) {
    return "--";
  }
  const caseTypeText = caseTypeLabel(caseType);
  switch (caseType) {
    case "FAX_INTAKE":
      return (
        <div style={{ display: "inline-flex" }}>
          <Print style={{ color: colorsLight.font.secondary, marginRight: 12 }} />
          <div style={{ marginBottom: 8 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
    case "RN_REVIEW":
      return (
        <div style={{ display: "inline-flex" }}>
          <ClipboardIcon />
          <div style={{ marginBottom: 8, marginLeft: 12 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
    case "MD_REVIEW":
      return (
        <div style={{ display: "inline-flex" }}>
          <MedicalServicesIcon />
          <div style={{ marginBottom: 8, marginLeft: 12 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
    case "LETTER_WRITING":
      return (
        <div style={{ display: "inline-flex" }}>
          <LetterWritingIcon />
          <div style={{ marginBottom: 8, marginLeft: 12 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
    case "MISSING_INFORMATION":
      return (
        <div style={{ display: "inline-flex" }}>
          <Phone style={{ color: colorsLight.font.secondary, marginRight: 12 }} />
          <div style={{ marginBottom: 8 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
    default:
      return (
        <div style={{ display: "inline-flex" }}>
          <div style={{ marginBottom: 8 }}>
            <Caption style={{ color: colorsLight.font.secondary }}>{caseTypeText}</Caption>
          </div>
        </div>
      );
  }
};

/**
 * The "Escalated" chip should only be displayed if the service case is escalated, the queue is configured to
 * display the chip, and the user is an internal Cohere employee
 */
const shouldDisplayEscalatedChip = (serviceCase: ServiceCase, isCohereUser: boolean, showEscalatedChip: boolean) => {
  return isCohereUser && showEscalatedChip && serviceCase.isEscalated;
};

const dueDateValueWithOptionalEscalatedChip = (
  serviceCase: ServiceCase,
  isCohereUser: boolean,
  displayConfig?: DisplayConfig
) => {
  const { showEscalatedChip } = displayConfig || {};
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }} className="MuiTypography-body1">
      {formatDateStrWithCustomFormat(serviceCase.dueDate, "MM/dd/yyyy - hh:mma")}
      {shouldDisplayEscalatedChip(serviceCase, isCohereUser, !!showEscalatedChip) ? (
        <Pill variant="error" label="Escalated" />
      ) : null}
    </div>
  );
};

/**
 * The "Expedited" chip should only be displayed if the service case is escalated and the user is an internal Cohere employee
 */
const shouldDisplayExpeditedChip = (serviceCase: ServiceCase, isCohereUser: boolean, showExpeditedChip: boolean) => {
  return isCohereUser && showExpeditedChip && serviceCase.isFlaggedAsUrgent;
};

const isFlaggedAsUrgentChip = (serviceCase: ServiceCase, isCohereUser: boolean, displayConfig?: DisplayConfig) => {
  const { showExpeditedChip } = displayConfig || {};
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }} className="MuiTypography-body1">
      {shouldDisplayExpeditedChip(serviceCase, isCohereUser, !!showExpeditedChip) ? (
        <Pill variant="error" label="Expedited" />
      ) : null}
    </div>
  );
};

const workCompleteValue = (serviceCase: ServiceCase, displayConfig?: DisplayConfig) => {
  const caseOutcome = getCaseOutcomeMapping(serviceCase);
  const { hideReviewOutcomeChip, showAuditOutcomeChip } = displayConfig || {};
  return (
    <div style={{ display: "inline-flex", gap: 8 }} className="MuiTypography-body1">
      {serviceCase.caseStatus === "COMPLETE"
        ? formatDateStrWithCustomFormat(serviceCase.lastUpdated, "MM/dd/yyyy - hh:mma")
        : "--"}
      {isReviewCase(serviceCase) ? (
        <>
          {(isAuditCase(serviceCase) || !!showAuditOutcomeChip) && serviceCase.outcome?.auditOutcome && (
            <AuditCompletionChipMapping auditOutcome={serviceCase.outcome?.auditOutcome} />
          )}
          {!hideReviewOutcomeChip && serviceCase.outcome?.reviewOutcome && (
            <ReviewCompletionChipMapping reviewOutcome={serviceCase.outcome?.reviewOutcome} label={caseOutcome} />
          )}
        </>
      ) : (
        <>{caseOutcome !== "" && <Pill variant="primary" label={caseOutcome} />}</>
      )}
    </div>
  );
};

const getCaseOutcomeMapping = (serviceCase: ServiceCase) =>
  isReviewCase(serviceCase) && serviceCase.outcome?.reviewOutcome
    ? getReviewCaseOutcomeMapping(serviceCase.healthPlan)[serviceCase.outcome.reviewOutcome]
    : faxIntakeCaseOutcomeMapping(serviceCase);

function outreachLabel(attemptNumber: number): string {
  return attemptNumber > 0 ? "MI " + getOrdinalString(attemptNumber) + " attempt" : "MI no attempt yet";
}

const outreachStatusValue = (
  serviceCase: ServiceCase,
  missingInfoConfigs: Record<string, MissingInformationOutreachConfigurationResponse>
) => {
  if (missingInfoConfigs) {
    const configKey = `${serviceCase.healthPlan}${
      serviceCase.delegatedVendor ? `_${serviceCase.delegatedVendor}` : ""
    }`;
    const matchingMissingInfoConfig = missingInfoConfigs[configKey];

    if (matchingMissingInfoConfig && matchingMissingInfoConfig.allowSendManualOutreachFromPortal) {
      const outreachAttemptStatuses = serviceCase.serviceRequest?.previousMICOutreachAttempts ?? [];
      const maxAutomatedAttempts = getMaxAutomatedAttempts(matchingMissingInfoConfig);
      const sortedAttempts = [...outreachAttemptStatuses].sort((a, b) => {
        if (!a.dateCreated && !b.dateCreated) {
          return 0;
        }
        if (!a.dateCreated) {
          return 1;
        }
        if (!b.dateCreated) {
          return -1;
        }
        return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
      });
      // Only automated attempts
      const automatedAttempts = sortedAttempts.filter((a) => a.manual === false);
      const lastAutomatedAttempts = automatedAttempts.slice(0, maxAutomatedAttempts);

      // If last automated attempts all failed and manual outreach is allowed → show error pill
      if (
        lastAutomatedAttempts.length >= maxAutomatedAttempts &&
        lastAutomatedAttempts.every((a) => a.outreachOutcome === "FAILED")
      ) {
        return (
          <Body1 style={{ display: "inline-flex", gap: 8 }}>
            <Pill label="Automation failed" variant="error" />
          </Body1>
        );
      }
      // fallback to default pill UI (old behavior)
    }
  }

  const outreachType =
    serviceCase.serviceRequest?.lastSuccessfulMICOutreachAttempt?.outreachType || "MISSING_INFO_CLINICAL";
  const outreachAttemptNumber: number =
    serviceCase.serviceRequest?.lastSuccessfulMICOutreachAttempt?.outreachAttemptNumber || 0;
  const label = outreachLabel(outreachAttemptNumber);

  return (
    <Body1 style={{ display: "inline-flex", gap: 8 }}>
      {outreachType === "MISSING_INFO_CLINICAL" && <Pill label={label} variant="info" />}
    </Body1>
  );
};

function getMaxAutomatedAttempts(missingInfoConfig: MissingInformationOutreachConfigurationResponse) {
  if (!missingInfoConfig.missingInfoOutreachAttempts) {
    return 0;
  }

  let maxAttempts = 0;

  missingInfoConfig.missingInfoOutreachAttempts.forEach((attempt) => {
    attempt.missingInfoOutreaches?.forEach((outreach) => {
      const attemptCount = outreach.attemptConfigs?.length || 0;
      maxAttempts = Math.max(maxAttempts, attemptCount);
    });
  });

  return maxAttempts;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    widePill: {
      padding: theme.spacing(0.625, 1, 0.5),
      height: 24,
      marginTop: -2,
    },
  })
);

const AuditCompletionChipMapping: FunctionComponent<React.PropsWithChildren<{ auditOutcome: AuditOutcome }>> = ({
  auditOutcome,
}) => {
  const classes = useStyles();
  if (auditOutcome === "PASS") {
    return <Pill label={auditOutcomeMapping[auditOutcome]} variant="success" className={classes.widePill} />;
  } else if (auditOutcome === "WITHDRAWN") {
    return <Pill label={auditOutcomeMapping[auditOutcome]} variant="info" className={classes.widePill} />;
  } else {
    return <Pill label={auditOutcomeMapping[auditOutcome]} variant="warning" className={classes.widePill} />;
  }
};

const ReviewCompletionChipMapping: FunctionComponent<
  React.PropsWithChildren<{ reviewOutcome: ReviewOutcome; label: string }>
> = ({ reviewOutcome, label }) => {
  const classes = useStyles();
  if (reviewOutcome === "APPROVED") {
    return <Pill label={label} variant="success" className={classes.widePill} />;
  } else if (reviewOutcome === "WITHDRAWN") {
    return <Pill label={label} variant="info" className={classes.widePill} />;
  } else {
    return <Pill label={label} variant="warning" className={classes.widePill} />;
  }
};

const faxIntakeCaseOutcomeMapping = (serviceCase: ServiceCase) => {
  switch (serviceCase?.outcome?.outcome) {
    case "NEW_SR":
      return "Request created: " + serviceCase.outcome?.serviceRequests?.map((x) => x.cohereId).join(", ");
    case "FAX_ATTACHED":
      return "Attached to: " + serviceCase.outcome?.serviceRequests?.map((x) => x.cohereId).join(", ");
    case "FAX_ATTACHED_TO_RF":
      return "Attached to: " + serviceCase.outcome?.referralRequest?.cohereId;
    case "FAXBACK":
      return "Fax notice sent";
    case "CLAIM":
      return "Claim resolved";
    case "HEALTH_HELP":
      return "Health Help resolved";
    case "ANOTHER_SYSTEM":
      return "Case resolved in another system";
    case "EDIT_EXISTING_SR":
      return "Edited SR: " + serviceCase.outcome?.serviceRequests?.map((x) => x.cohereId).join(", ");
    case "FORWARDED":
      return "Fax forwarded: " + serviceCase.outcome?.faxForwardLine;
    case "NEW_REFERRAL":
      return "Referral created: " + serviceCase.outcome?.referralRequest?.cohereId;
    case "SPLIT_FAX":
      return "Split Fax";
    default:
      return "";
  }
};

export const getReviewCaseOutcomeMapping = (healthPlan?: string): Record<ReviewOutcome, string> => {
  const isMedicareNovitas = healthPlan === "MEDICARE_NOVITAS";

  return {
    WITHDRAWN: "Withdrawn",
    APPROVED: isMedicareNovitas ? "Affirmed" : "Approved",
    PENDING_ADMIN_VOID: "Pending admin void",
    PENDING_MD_REVIEW: "Pending MD review",
    PENDING_P2P_REVIEW: "Pending peer-to-peer review",
    PENDING_MISSING_CLINICAL_INFO: "Pending missing info",
    PENDING_EXTERNAL_REVIEW: "Pending external review",
    PENDING_SCHEDULING_OUTREACH: "Pending peer to peer scheduling",
    PENDING_NUDGE_OUTREACH: "Pending nudge outreach",
    PENDING_RN_REVIEW: "Pending clinical review",
    RECOMMENDED_DENIAL: "Recommended Denial",
    RECOMMENDED_PARTIAL_APPROVAL: "Recommended Partial Approval",
    PARTIALLY_APPROVED: "Partially approved",
    RECOMMENDED_APPROVAL: "Recommended approval",
    DENIED: isMedicareNovitas ? "Non-affirmed" : "Denied",
    MISROUTE: "Misrouted",
    UNWORKABLE: "Unworkable",
    OUT_OF_SCOPE: "Pending out of scope audit",
    ADMINISTRATIVE_DENIAL: "Administrative denial",
  };
};

const auditOutcomeMapping: Record<AuditOutcome, string> = {
  PASS: "Passed audit",
  FAIL: "Failed audit",
  WITHDRAWN: "Withdrawn",
};

const isReviewCase = (serviceCase: ServiceCase) => {
  return serviceCase.caseType !== "FAX_INTAKE";
};

const isAuditCase = (serviceCase: ServiceCase) => {
  return serviceCase.caseType === "NEW_HIRE_AUDIT_REVIEW";
};

const timeSinceOutreach = (outreachDate: string): string => {
  const timeSinceOutreach = diffDatefromNow(outreachDate);
  let diff = timeSinceOutreach.minutes + "m";
  if (timeSinceOutreach.hours > 0) {
    diff = timeSinceOutreach.hours + "h : " + diff;
  } else if (timeSinceOutreach.days > 0) {
    diff = "0h : " + diff;
  }
  if (timeSinceOutreach.days > 0) {
    diff = timeSinceOutreach.days + "d : " + diff;
  }
  return diff;
};

export function getOrdinalString(n: number | undefined): string {
  if (n) {
    let suffix = "";
    const x = n % 100;
    if (11 <= x && x <= 13) {
      suffix = "th";
    } else {
      suffix = ["th", "st", "nd", "rd", "th"][Math.min(n % 10, 4)];
    }

    return n.toString() + suffix;
  } else {
    return "";
  }
}

const hasNewAttachmentsValue = (serviceCase: ServiceCase) => {
  const { hasNewAttachments } = serviceCase;

  return hasNewAttachments ? (
    <Chip label="New attachment" size="small" type="warning" />
  ) : (
    <Chip label="No new attachments" size="small" type="neutral" />
  );
};

const toSentenceCase = (str: string): string => {
  if (str) {
    let s = str.toLowerCase();
    s = s.replace(/_/g, " ");
    return s.slice(0, 1).toUpperCase() + s.slice(1);
  }

  return "";
};
